import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import Button from "../../ui/Button";
import PhoneFrame from "./PhoneFrame";

const CHECK_KEYS = ["check1", "check2", "check3", "check4"];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.6, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function Demo() {
  const { t } = useTranslation("landing");

  return (
    <section
      id="demo"
      className="scroll-mt-24 bg-bg-2 py-16 sm:py-20 lg:py-24"
    >
      <div className="mx-auto max-w-container px-5 sm:px-7">
        <div className="grid grid-cols-1 items-center gap-14 lg:grid-cols-2 lg:gap-16 xl:gap-20">
          {/* ---------- Text side ---------- */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            className="text-center lg:text-start"
          >
            <motion.div
              variants={fadeUp}
              custom={0}
              className="mb-4 text-[12px] font-extrabold uppercase tracking-[0.25em] text-teal-2 sm:text-[13px]"
            >
              {t("demo.eyebrow")}
            </motion.div>

            <motion.h2
              variants={fadeUp}
              custom={1}
              className="mb-5 text-[28px] font-black leading-[1.2] tracking-tight text-ink sm:text-[36px] lg:text-[42px]"
            >
              <span>{t("demo.title1")} </span>
              <span className="bg-gradient-to-br from-teal to-gold bg-clip-text text-transparent">
                {t("demo.titleGrad")}
              </span>
            </motion.h2>

            <motion.p
              variants={fadeUp}
              custom={2}
              className="mx-auto mb-8 max-w-[520px] text-[15.5px] leading-[1.95] text-ink-soft sm:text-[16.5px] lg:mx-0"
            >
              {t("demo.desc")}
            </motion.p>

            <motion.ul
              variants={fadeUp}
              custom={3}
              className="mb-10 flex flex-col gap-3.5 text-start"
            >
              {CHECK_KEYS.map((key) => (
                <li
                  key={key}
                  className="flex items-center gap-3 text-[14.5px] font-medium text-ink sm:text-[15px]"
                >
                  <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-gradient-to-br from-teal to-teal-2 text-white shadow-[0_6px_16px_rgba(15,118,110,0.35)]">
                    <Check className="h-3.5 w-3.5" strokeWidth={3} />
                  </span>
                  {t(`demo.${key}`)}
                </li>
              ))}
            </motion.ul>

            <motion.div variants={fadeUp} custom={4}>
              <Button as={Link} to="/login" variant="primary" size="lg">
                {t("demo.cta")} →
              </Button>
            </motion.div>
          </motion.div>

          {/* ---------- Phone side ---------- */}
          <div className="relative">
            {/* Glow behind phone */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 m-auto h-[400px] w-[400px] rounded-full bg-teal-2/20 blur-[100px]"
            />
            <PhoneFrame />
          </div>
        </div>
      </div>
    </section>
  );
}